import { supabase } from '../supabaseClient';
import { getTransactionsPeriodiques, TransactionPeriodiqueWithDetails } from './PeriodicTransactionService';
import { format } from 'date-fns';

// Interface pour les résultats de génération
export interface GenerationResult {
  totalGenerated: number;
  alreadyExisting: number;
  errors: number;
  details: string[];
}

/**
 * Vérifie si une transaction périodique a déjà été générée pour un mois donné
 * @param transactionPeriodiqueId ID de la transaction périodique
 * @param year Année cible
 * @param month Mois cible (0-11)
 * @returns true si la transaction existe déjà
 */
export const checkTransactionExists = async (
  transactionPeriodiqueId: string,
  year: number,
  month: number
): Promise<boolean> => {
  try {
    // Définir la période du mois
    const startDate = new Date(year, month, 1).toISOString().split('T')[0]; // 1er du mois
    const endDate = new Date(year, month + 1, 0).toISOString().split('T')[0]; // Dernier jour du mois
    
    // Récupérer les transactions pour ce mois qui ont été générées à partir de cette transaction périodique
    const { data, error } = await supabase
      .from('transactions')
      .select('*')
      .eq('transaction_periodique_id', transactionPeriodiqueId)
      .gte('date', startDate)
      .lte('date', endDate);
      
    if (error) {
      console.error('Erreur lors de la vérification des transactions existantes:', error);
      return false;
    }
    
    return data && data.length > 0;
  } catch (error) {
    console.error('Exception dans checkTransactionExists:', error);
    return false;
  }
};

/**
 * Crée une nouvelle transaction
 * @param transaction Données de la transaction à créer
 * @returns Résultat de la création
 */
const createTransaction = async (transaction: any) => {
  try {
    // Vérification de la session
    const { data: sessionData } = await supabase.auth.getSession();
    if (!sessionData?.session) {
      return { data: null, error: { message: 'Aucune session active' } };
    }
    const userId = sessionData.session.user.id;

    const { data, error } = await supabase
      .from('transactions')
      .insert({ ...transaction, user_id: userId })
      .select()
      .single();

    return { data, error };
  } catch (error) {
    console.error('Exception dans createTransaction:', error);
    return { data: null, error };
  }
};

/**
 * Génère les transactions périodiques actives pour un mois donné
 * @param year Année cible
 * @param month Mois cible (0-11)
 * @returns Résultat de la génération
 */
export const generateTransactionsForMonth = async (
  year: number,
  month: number
): Promise<GenerationResult> => {
  const result: GenerationResult = {
    totalGenerated: 0,
    alreadyExisting: 0,
    errors: 0,
    details: []
  };
  
  try {
    // Récupérer toutes les transactions périodiques actives
    const { data, error } = await getTransactionsPeriodiques();
    
    if (error) {
      console.error('Erreur lors de la récupération des transactions périodiques:', error);
      result.errors++;
      result.details.push('Erreur lors de la récupération des transactions périodiques');
      return result;
    }
    
    const transactions: TransactionPeriodiqueWithDetails[] = (data as TransactionPeriodiqueWithDetails[]) || [];

    if (!transactions || transactions.length === 0) {
      result.details.push('Aucune transaction périodique active trouvée');
      return result;
    }
    
    // Filtrer les transactions actives
    const activeTransactions = transactions.filter((t: TransactionPeriodiqueWithDetails) => !!t.actif);
    
    // Pour chaque transaction périodique active
    for (const transaction of activeTransactions) {
      try {
        // Vérifier si la transaction existe déjà pour ce mois
        const exists = await checkTransactionExists(transaction.id!, year, month);
        
        if (exists) {
          result.alreadyExisting++;
          result.details.push(`La transaction "${transaction.description}" existe déjà pour ce mois`);
          continue;
        }
        
        // Déterminer la date d'exécution pour ce mois
        let executionDate: Date;
        
        switch (transaction.frequence) {
          case 'quotidienne':
            // Pour les transactions quotidiennes, on utilise le 1er du mois
            executionDate = new Date(year, month, 1);
            break;
            
          case 'hebdomadaire':
            // Pour les transactions hebdomadaires, on trouve le premier jour de la semaine spécifiée dans le mois
            if (transaction.jour_semaine !== undefined) {
              executionDate = new Date(year, month, 1);
              // Trouver le premier jour de la semaine spécifiée dans le mois
              while (executionDate.getDay() !== transaction.jour_semaine) {
                executionDate.setDate(executionDate.getDate() + 1);
              }
            } else {
              // Par défaut, utiliser le 1er du mois
              executionDate = new Date(year, month, 1);
            }
            break;
            
          case 'mensuelle':
            // Pour les transactions mensuelles, on utilise le jour d'exécution spécifié
            if (transaction.jour_execution !== undefined) {
              // S'assurer que le jour est valide pour ce mois
              const lastDayOfMonth = new Date(year, month + 1, 0).getDate();
              const day = Math.min(transaction.jour_execution, lastDayOfMonth);
              executionDate = new Date(year, month, day);
            } else {
              // Par défaut, utiliser le 1er du mois
              executionDate = new Date(year, month, 1);
            }
            break;
            
          case 'trimestrielle':
            // Pour les transactions trimestrielles, vérifier si ce mois est un mois d'exécution
            // (janvier, avril, juillet, octobre ou selon la date de début)
            if ((month % 3) === 0) {
              if (transaction.jour_execution !== undefined) {
                const lastDayOfMonth = new Date(year, month + 1, 0).getDate();
                const day = Math.min(transaction.jour_execution, lastDayOfMonth);
                executionDate = new Date(year, month, day);
              } else {
                executionDate = new Date(year, month, 1);
              }
            } else {
              // Ce n'est pas un mois d'exécution pour cette transaction trimestrielle
              result.details.push(`La transaction trimestrielle "${transaction.description}" n'est pas prévue pour ce mois`);
              continue;
            }
            break;
            
          case 'annuelle':
            // Pour les transactions annuelles, vérifier si ce mois est le mois d'exécution
            if (transaction.mois_execution === month) {
              if (transaction.jour_execution !== undefined) {
                const lastDayOfMonth = new Date(year, month + 1, 0).getDate();
                const day = Math.min(transaction.jour_execution, lastDayOfMonth);
                executionDate = new Date(year, month, day);
              } else {
                executionDate = new Date(year, month, 1);
              }
            } else {
              // Ce n'est pas le mois d'exécution pour cette transaction annuelle
              result.details.push(`La transaction annuelle "${transaction.description}" n'est pas prévue pour ce mois`);
              continue;
            }
            break;
            
          default:
            // Par défaut, utiliser le 1er du mois
            executionDate = new Date(year, month, 1);
        }
        
        // Créer la nouvelle transaction
        // Ne pas reprendre attachment_path : l'icône périodique n'est pas une PJ de transaction
        const newTransaction = {
          date: executionDate.toISOString().split('T')[0],
          description: transaction.description,
          montant: transaction.montant,
          type: transaction.type,
          categorie_id: transaction.categorie_id || '',
          compte_id: transaction.compte_id,
          compte_destination_id: transaction.compte_destination_id,
          transaction_periodique_id: transaction.id, // Référence à la transaction périodique source
        };
        
        // Ajouter la transaction
                 const { error } = await createTransaction(newTransaction);
        
        if (error) {
          console.error(`Erreur lors de la création de la transaction "${transaction.description}":`, error);
          result.errors++;
          result.details.push(`Erreur lors de la création de la transaction "${transaction.description}": ${(error as any)?.message || 'Erreur inconnue'}`);
        } else {
          result.totalGenerated++;
          result.details.push(`Transaction "${transaction.description}" créée avec succès pour le ${format(executionDate, 'dd/MM/yyyy')}`);
        }
      } catch (error) {
        console.error(`Exception lors du traitement de la transaction "${transaction.description}":`, error);
        result.errors++;
        result.details.push(`Exception lors du traitement de la transaction "${transaction.description}"`);
      }
    }
    
    return result;
  } catch (error) {
    console.error('Exception dans generateTransactionsForMonth:', error);
    result.errors++;
    result.details.push('Exception générale lors de la génération des transactions');
    return result;
  }
}; 